import { trackInAnalyticsAddToCart, trackPiwikCartUpdate } from '@ifixit/analytics';
import { useTranslations } from '@ifixit/i18n';
import {
   type CartWarning,
   type CountryCode,
   type DisplayableError,
   type LanguageCode,
   StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import {
   convertAddToCartInputToAnalyticsItemEvent,
   convertCartLineItemsToAnalyticsItem,
   trackAnalyticsShopifyAddToCart,
} from '../../helpers/analytics';
import { addToCart, getCart, updateCartLines } from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import { CartMutationType, type Cart, type CartLineItem } from '../../types';
import { useCart, useCartToasts } from './use-cart';
import { useCreateCart } from './use-create-cart';
import { useCartKeys } from './use-cart-keys';

export type AddToCartDescriptor =
   | 'PDP Main'
   | 'PDP Featured Products'
   | 'PDP Floating Bar'
   | 'Cart Page Cross Sell'
   | 'Cart Page Merch'
   | 'Cart Drawer Cross Sell'
   | 'Cart Drawer Merch'
   | 'Frequently Bought Together'
   | 'Guide Products'
   | 'Guide Step'
   | 'Answers Product Ad'
   | 'Product Ad Cards - Answers'
   | 'Product Ad Cards - Guide'
   | 'Buy it Again'
   | 'Cart Permalink'
   | 'Product Comparison';

type AddToCartAnalytics =
   | {
        descriptor: Exclude<AddToCartDescriptor, 'Frequently Bought Together'>;
        localeCode?: string;
     }
   | {
        descriptor: Extract<AddToCartDescriptor, 'Frequently Bought Together'>;
        currentItemcode: string;
        localeCode?: string;
     };

export type AddToCartInput = {
   lines: CartLineItem[];
   analytics: AddToCartAnalytics;
};

export function useAddToCart() {
   const { mutationKey, queryKey } = useCartKeys();
   const cart = useCart().data;
   const createCart = useCreateCart();
   const isMutating = useIsMutating({
      mutationKey,
      predicate: mutation => mutation.meta?.type !== CartMutationType.AddToCart,
   });
   const queryClient = useQueryClient();
   const {
      client: storefrontClient,
      country,
      currencyCode,
      language,
   } = useShopifyStorefrontClient();
   const t = useTranslations();
   const { showCartWarnings, showErrorToast } = useCartToasts({
      title: t('ErrorHandling.unableToAddProduct'),
   });
   const mutation = useMutation({
      mutationKey,
      meta: { type: CartMutationType.AddToCart },
      mutationFn: async ({ lines }: AddToCartInput) => {
         if (lines.length === 0 || cart == null) {
            return buildIfixitCart({
               cart: null,
               fallbackCurrencyCode: currencyCode,
               userErrors: [],
               validationErrors: [],
               warnings: [],
            });
         }

         let newCart = null;
         let userErrors: DisplayableError[] = [];
         let validationErrors: string[] = [];
         let warnings: CartWarning[] = [];

         if (cart.shopifyCartId) {
            const result = await performAddToCart({
               cartId: cart.shopifyCartId,
               client: storefrontClient,
               country,
               lines,
               language,
            });
            newCart = result.cart;
            userErrors = result.userErrors;
            validationErrors = result.validationErrors;
            warnings = result.warnings;
         }

         if (!cart.shopifyCartId || !newCart) {
            const result = await createCart({
               country,
               lines: lines.map(({ quantity, shopifyVariantId }) => ({
                  merchandiseId: shopifyVariantId,
                  quantity,
               })),
               language,
            });
            newCart = result.cart;
            userErrors = result.userErrors;
            validationErrors = result.validationErrors;
            warnings = result.warnings;
         }

         return buildIfixitCart({
            cart: newCart,
            fallbackCurrencyCode: currencyCode,
            userErrors,
            validationErrors,
            warnings,
         });
      },
      onMutate: async ({ lines }: AddToCartInput) => {
         await queryClient.cancelQueries({ queryKey });
         window.onbeforeunload = () =>
            "Some products are being added to the cart. Are you sure you'd like to cancel?";

         const previousCart = queryClient.getQueryData<Cart>(queryKey);

         queryClient.setQueryData<Cart | undefined>(queryKey, currentCart => {
            if (currentCart == null) {
               return currentCart;
            }
            const updatedCart = lines.reduce((cart, line) => addLineItem(cart, line), currentCart);

            return updatedCart;
         });

         return { previousCart };
      },
      onError: (error, variables, context) => {
         queryClient.setQueryData<Cart | undefined>(queryKey, context?.previousCart);
         showErrorToast();
      },
      onSuccess: async (cart, variables) => {
         // Only update the cart state if we are the only mutation running.
         if (queryClient.isMutating({ mutationKey }) === 1) {
            queryClient.setQueryData<Cart | undefined>(queryKey, cart);
         }
         showCartWarnings({ cart, type: CartMutationType.AddToCart, variables });

         trackAnalyticsShopifyAddToCart(variables);
         const event = convertAddToCartInputToAnalyticsItemEvent(variables);
         trackInAnalyticsAddToCart(event);
         trackPiwikCartUpdate({
            items: convertCartLineItemsToAnalyticsItem(cart.lineItems),
            value: Number(cart.totals.price.amount),
            currency: cart.totals.price.currencyCode,
            localeCode: variables.analytics.localeCode,
         });
      },
      onSettled: () => {
         window.onbeforeunload = () => {};
      },
   });
   return { addToCart: mutation, enabled: cart != null && !isMutating };
}

export async function performAddToCart({
   cartId,
   client,
   country,
   lines,
   language,
}: {
   client: StorefrontClient;
   cartId: string;
   country: CountryCode;
   lines: CartLineItem[];
   language: LanguageCode;
}) {
   const cart = await getCart(client, { cartId, country, language });
   const lineEdges = cart?.lines.edges ?? [];
   const linesToUpdate =
      cart == null
         ? []
         : lines
              .map(line => {
                 const existingLine = lineEdges.find(
                    ({ node }) => node.merchandise.id === line.shopifyVariantId
                 )?.node;
                 const existingQuantity = existingLine?.quantity ?? 0;
                 return existingLine?.id
                    ? {
                         id: existingLine.id,
                         merchandiseId: line.shopifyVariantId,
                         quantity: existingQuantity + line.quantity,
                      }
                    : null;
              })
              .filter(<T,>(line: T | null): line is T => line != null);
   const linesToAdd = lines
      .filter(line => !lineEdges.some(({ node }) => node.merchandise.id === line.shopifyVariantId))
      .map(({ shopifyVariantId, quantity }) => ({
         merchandiseId: shopifyVariantId,
         quantity,
      }));

   let newCart = cart;
   let userErrors: DisplayableError[] = [];
   let validationErrors: string[] = [];
   let warnings: CartWarning[] = [];

   if (linesToUpdate.length > 0) {
      const result = await updateCartLines(client, {
         cartId,
         lines: linesToUpdate,
         country,
         language,
      });
      newCart = result.cart ?? null;
      userErrors = [...userErrors, ...result.userErrors];
      validationErrors = [...validationErrors, ...result.validationErrors];
      warnings = [...warnings, ...result.warnings];
   }

   if (linesToAdd.length > 0) {
      const result = await addToCart(client, { cartId, lines: linesToAdd, country, language });
      newCart = result.cart ?? null;
      userErrors = [...userErrors, ...result.userErrors];
      validationErrors = [...validationErrors, ...result.validationErrors];
      warnings = [...warnings, ...result.warnings];
   }

   newCart ??= userErrors.length > 0 || validationErrors.length > 0 ? cart : null;

   return { cart: newCart, userErrors, validationErrors, warnings };
}

function addLineItem(cart: Cart, inputLineItem: CartLineItem): Cart {
   const currentLineItemIndex = cart.lineItems.findIndex(
      item => item.itemcode === inputLineItem.itemcode
   );
   const isAlreadyInCart = currentLineItemIndex !== -1;
   const updatedLineItems = [...cart.lineItems];
   if (isAlreadyInCart) {
      const currentLineItem = cart.lineItems[currentLineItemIndex];
      const updatedQuantity = currentLineItem.quantity + inputLineItem.quantity;
      updatedLineItems.splice(currentLineItemIndex, 1, {
         ...currentLineItem,
         quantity: updatedQuantity,
      });
   } else {
      // Match Shopify's behavior of prependng new items to the cart
      updatedLineItems.unshift(inputLineItem);
   }
   const updateTotalPrice = Math.max(
      Number(cart.totals.price.amount) +
         inputLineItem.quantity * Number(inputLineItem.price.amount),
      0
   ).toFixed(2);
   return {
      ...cart,
      hasItemsInCart: true,
      isEmpty: false,
      lineItems: updatedLineItems,
      totals: {
         ...cart.totals,
         price: {
            ...cart.totals.price,
            amount: updateTotalPrice,
         },
         itemsCount: cart.totals.itemsCount + inputLineItem.quantity,
      },
   };
}
